import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Epic, EpicRoadmapData } from 'shared/types/epic';
import * as Sentry from '@sentry/react';

export interface CreateEpicDto {
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  ownerId?: string;
}

export interface UpdateEpicDto {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  ownerId?: string;
}

/**
 * Fetch all epics
 */
export const getAllEpics = async (session: any): Promise<Epic[]> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics`,
      { method: 'GET' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error('Failed to fetch epics'), {
        extra: { status: response.status, error: errorData }
      });
      throw new Error('Failed to fetch epics');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'getAllEpics' }
    });
    throw error;
  }
};

/**
 * Fetch a single epic by ID
 */
export const getEpic = async (id: number, session: any): Promise<Epic> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${id}`,
      { method: 'GET' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error(`Failed to fetch epic ${id}`), {
        extra: { epicId: id, status: response.status, error: errorData }
      });
      throw new Error('Failed to fetch epic');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'getEpic' },
      extra: { epicId: id }
    });
    throw error;
  }
};

/**
 * Fetch epic with progress/roadmap data
 */
export const getEpicRoadmap = async (
  id: number,
  session: any
): Promise<EpicRoadmapData> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${id}/roadmap`,
      { method: 'GET' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error(`Failed to fetch epic roadmap ${id}`), {
        extra: { epicId: id, status: response.status, error: errorData }
      });
      throw new Error('Failed to fetch epic roadmap');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'getEpicRoadmap' },
      extra: { epicId: id }
    });
    throw error;
  }
};

/**
 * Create a new epic
 */
export const createEpic = async (
  dto: CreateEpicDto,
  session: any
): Promise<Epic> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto)
      },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error('Failed to create epic'), {
        extra: { dto, status: response.status, error: errorData }
      });
      throw new Error('Failed to create epic');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'createEpic' },
      extra: { dto }
    });
    throw error;
  }
};

/**
 * Update an existing epic
 */
export const updateEpic = async (
  id: number,
  dto: UpdateEpicDto,
  session: any
): Promise<Epic> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dto)
      },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error(`Failed to update epic ${id}`), {
        extra: { epicId: id, dto, status: response.status, error: errorData }
      });
      throw new Error('Failed to update epic');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'updateEpic' },
      extra: { epicId: id, dto }
    });
    throw error;
  }
};

/**
 * Delete an epic (unlinks all features)
 */
export const deleteEpic = async (id: number, session: any): Promise<void> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${id}`,
      { method: 'DELETE' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(new Error(`Failed to delete epic ${id}`), {
        extra: { epicId: id, status: response.status, error: errorData }
      });
      throw new Error('Failed to delete epic');
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'deleteEpic' },
      extra: { epicId: id }
    });
    throw error;
  }
};

/**
 * Add a feature (bug report) to an epic
 */
export const addFeatureToEpic = async (
  epicId: number,
  bugId: number,
  session: any
): Promise<Epic> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${epicId}/add-feature/${bugId}`,
      { method: 'PUT' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(
        new Error(`Failed to add feature ${bugId} to epic ${epicId}`),
        {
          extra: {
            epicId,
            bugId,
            status: response.status,
            error: errorData
          }
        }
      );
      throw new Error('Failed to add feature to epic');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'addFeatureToEpic' },
      extra: { epicId, bugId }
    });
    throw error;
  }
};

/**
 * Remove a feature (bug report) from an epic
 */
export const removeFeatureFromEpic = async (
  epicId: number,
  bugId: number,
  session: any
): Promise<Epic> => {
  try {
    const response = await fetchWithAuth(
      `${API_URL}/epics/${epicId}/remove-feature/${bugId}`,
      { method: 'PUT' },
      session
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Sentry.captureException(
        new Error(`Failed to remove feature ${bugId} from epic ${epicId}`),
        {
          extra: {
            epicId,
            bugId,
            status: response.status,
            error: errorData
          }
        }
      );
      throw new Error('Failed to remove feature from epic');
    }

    return await response.json();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { service: 'epicService', method: 'removeFeatureFromEpic' },
      extra: { epicId, bugId }
    });
    throw error;
  }
};
